/*
 * Keeps the current FullStory session ID on the Shopify cart as the
 * `fs_session_id` attribute, so Shopify Flow can bind the server-side
 * `Order Completed` event back to the session that placed the order.
 *
 * Design: docs/superpowers/specs/2026-07-31-fullstory-server-side-revenue-design.md
 *
 * Two synchronisation points, no polling:
 *   1. FullStory start  — read the cart, write the attribute if it differs.
 *   2. Checkout submit  — cart attributes outlive a FullStory session, so
 *                         refresh a stale value before the customer leaves.
 *
 * FullStory failure is an analytics failure, never a commerce failure: every
 * path here either continues the checkout immediately or resumes it within
 * CHECKOUT_WAIT_MS.
 */
(function () {
  'use strict';

  var ATTRIBUTE = 'fs_session_id';
  var CART_FORM = 'form.cart.ajaxcart';
  var CHECKOUT_WAIT_MS = 1500;

  // The session ID we last observed on the cart. Null means unknown, not empty.
  var cartSessionId = null;
  // One cart write at a time — concurrent callers share this promise.
  var inFlight = null;
  // Guards the resumed native submission so it can't re-enter the listener.
  var resubmitting = false;

  // Locale-aware; the same theme serves two stores and Markets adds prefixes.
  function root() {
    var routes = window.Shopify && window.Shopify.routes;
    if (routes && routes.root) {
      return routes.root;
    }
    return window.shopUrl ? window.shopUrl + '/' : '/';
  }

  function debug(message, detail) {
    if (window.console && typeof console.debug === 'function') {
      console.debug('[fullstory-session] ' + message, detail);
    }
  }

  // Synchronous read. Returns '' when FullStory is absent, blocked, or has not
  // started capture — never a pending promise.
  function currentSession() {
    if (typeof window.FS !== 'function') {
      return '';
    }
    try {
      return window.FS('getSession', { format: 'id' }) || '';
    } catch (error) {
      debug('getSession failed', error);
      return '';
    }
  }

  function readCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('cart.js responded ' + response.status);
      }
      return response.json();
    });
  }

  function writeAttribute(sessionId) {
    var attributes = {};
    attributes[ATTRIBUTE] = sessionId;

    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('cart/update.js responded ' + response.status);
      }
      return response.json();
    }).then(function (cart) {
      cartSessionId = (cart && cart.attributes && cart.attributes[ATTRIBUTE]) || sessionId;
      return cartSessionId;
    });
  }

  /*
   * skipRead is used at checkout: the extra cart.js round trip would sit in
   * front of the customer, and we already know the cached value is stale.
   */
  function syncCart(sessionId, skipRead) {
    if (inFlight) {
      return inFlight;
    }
    if (cartSessionId === sessionId) {
      return Promise.resolve(sessionId);
    }

    var work;
    if (skipRead) {
      work = writeAttribute(sessionId);
    } else {
      work = readCart().then(function (cart) {
        var existing = cart && cart.attributes && cart.attributes[ATTRIBUTE];
        if (existing === sessionId) {
          cartSessionId = sessionId;
          return sessionId;
        }
        return writeAttribute(sessionId);
      });
    }

    inFlight = work.catch(function (error) {
      // A failed read on page load defers the attempt to checkout submission.
      debug('cart sync failed', error);
      return null;
    }).then(function (value) {
      inFlight = null;
      return value;
    });

    return inFlight;
  }

  /*
   * The start observer is preferred over getSessionAsync: FullStory documents
   * that the async promise can stay pending forever when a blocker stops the
   * capture script, and nothing here may hang on that.
   */
  function observeStart() {
    if (typeof window.FS !== 'function') {
      return;
    }
    try {
      window.FS('observe', {
        type: 'start',
        callback: function () {
          var sessionId = currentSession();
          if (sessionId) {
            syncCart(sessionId, false);
          }
        }
      });
    } catch (error) {
      debug('observe registration failed', error);
    }
  }

  function resumeSubmission(form, submitter) {
    resubmitting = true;

    if (submitter && typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      // Without requestSubmit, form.submit() drops the submitter — replace it
      // so the POST still carries the checkout intent rather than a cart update.
      if (submitter && submitter.name) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = submitter.name;
        hidden.value = submitter.value || '';
        form.appendChild(hidden);
      }
      form.submit();
    }

    // Release the guard once this submit event has finished dispatching, so a
    // submission blocked downstream doesn't disable syncing for the rest of
    // the page's life.
    window.setTimeout(function () {
      resubmitting = false;
    }, 0);
  }

  /*
   * The drawer form is static even though Handlebars replaces its contents, but
   * this is delegated on document anyway so it survives any re-render. It sits
   * above ajax-cart.js's body-level handler in the bubble path, so a submission
   * that handler has already blocked arrives here defaultPrevented.
   */
  function observeCheckout() {
    document.addEventListener('submit', function (event) {
      if (resubmitting || event.defaultPrevented) {
        return;
      }

      var form = event.target;
      if (!form || typeof form.matches !== 'function' || !form.matches(CART_FORM)) {
        return;
      }

      var sessionId = currentSession();
      if (!sessionId || cartSessionId === sessionId) {
        return;
      }

      event.preventDefault();

      var submitter = event.submitter || form.querySelector('[type="submit"][name="checkout"]');
      var resumed = false;
      var resume = function () {
        if (resumed) {
          return;
        }
        resumed = true;
        resumeSubmission(form, submitter);
      };

      // Hard deadline: checkout continues whether or not the write settles.
      window.setTimeout(resume, CHECKOUT_WAIT_MS);
      syncCart(sessionId, true).then(resume, resume);
    });
  }

  observeStart();
  observeCheckout();
})();
